// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import { useCallback } from "react";
import { LAYERS } from "../utils/scenarioEditorUtils";

export function useBackgroundImage({
  scenarioMetadata,
  setBackgroundImage,
  setLayerVisibility,
}) {
  const importBackgroundImage = useCallback(
    (event) => {
      const file = event.target.files?.[0];
      if (!file) return;
      try {
        if (
          file.type.includes("svg") ||
          file.name.toLowerCase().endsWith(".svg")
        ) {
          const reader = new FileReader();
          reader.onload = (e) => {
            try {
              const svgContent = e.target?.result;
              setLayerVisibility((prev) => ({
                ...prev,
                [LAYERS.BACKGROUND]: true,
              }));
              setBackgroundImage({
                type: "svg",
                source: "file",
                data: svgContent,
                width: scenarioMetadata.viewBox.width,
                height: scenarioMetadata.viewBox.height,
                filename: file.name,
              });
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error("Error processing SVG file:", error);
              // eslint-disable-next-line no-alert
              alert("Error processing SVG file.");
            }
          };
          reader.onerror = () => {
            // eslint-disable-next-line no-console
            console.error("Failed to read SVG file");
            // eslint-disable-next-line no-alert
            alert("Failed to read SVG file.");
          };
          reader.readAsText(file);
        } else {
          alert("Nur SVG-Dateien werden unterstützt (keine Pixelbilder).");
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error importing background image:", error);
        // eslint-disable-next-line no-alert
        alert("Error importing background image.");
      }
      if (event.target) {
        // eslint-disable-next-line no-param-reassign
        event.target.value = "";
      }
    },
    [scenarioMetadata.viewBox, setBackgroundImage, setLayerVisibility],
  );

  const importBackgroundFromURL = useCallback(
    async (url) => {
      if (!url.trim()) {
        // eslint-disable-next-line no-alert
        alert("Please enter a valid image URL.");
        return;
      }

      let parsedUrl;
      try {
        // eslint-disable-next-line no-new
        parsedUrl = new URL(url);
      } catch (error) {
        // eslint-disable-next-line no-alert
        alert("Please enter a valid URL.");
        return;
      }

      const pathname = (parsedUrl.pathname || "").toLowerCase();
      const hasSvgExtension = pathname.endsWith(".svg");

      try {
        const response = await fetch(url, { mode: "cors" });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const contentType = (
          response.headers.get("content-type") || ""
        ).toLowerCase();
        const isSvg = hasSvgExtension || contentType.includes("svg");

        if (isSvg) {
          const svgText = await response.text();
          setLayerVisibility((prev) => ({
            ...prev,
            [LAYERS.BACKGROUND]: true,
          }));
          setBackgroundImage({
            type: "svg",
            source: "url",
            data: svgText,
            width: scenarioMetadata.viewBox.width,
            height: scenarioMetadata.viewBox.height,
            url: parsedUrl.href,
            filename: pathname.split("/").pop() || "image.svg",
          });
          return;
        }

        const blob = await response.blob();
        const dataURL = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          setLayerVisibility((prev) => ({
            ...prev,
            [LAYERS.BACKGROUND]: true,
          }));
          setBackgroundImage({
            type: "image",
            source: "url",
            data: dataURL,
            width: img.width,
            height: img.height,
            originalWidth: img.width,
            originalHeight: img.height,
            url: parsedUrl.href,
            filename: pathname.split("/").pop() || "image",
          });
        };
        img.onerror = () => {
          // eslint-disable-next-line no-console
          console.error("Failed to load image from URL");
          // eslint-disable-next-line no-alert
          alert("Failed to load image. Please check the file format.");
        };
        img.src = URL.createObjectURL(blob);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to load background image:", error);
        // eslint-disable-next-line no-alert
        alert(`Failed to load image: ${error.message}`);
      }
    },
    [scenarioMetadata.viewBox, setLayerVisibility, setBackgroundImage],
  );

  return {
    importBackgroundImage,
    importBackgroundFromURL,
  };
}
